import React, { useEffect, useRef, useState, useCallback } from 'react'
import dayjs from 'dayjs'
import { Button, Checkbox, Modal, Pagination, Upload, message } from 'antd'
import sensors from '../../../utils/sensors'
import SpaceHeader from '../components/SpaceHeader'
import { usePersonalSpace } from '../hooks/usePersonalSpace'
import FilterBar from './components/FilterBar'
import PhotoGrid from './components/PhotoGrid'
import PhotoPreview from './components/PhotoPreview'
import VipGuard from '../components/VipGuard'
import DownloadModal, { DownloadConfig } from './components/DownloadModal'
import styles from './index.module.css'

const PersonalSpace: React.FC = () => {
  const {
    photos,
    total,
    page,
    pageSize,
    loading,
    watermarks,
    selectedIds,
    isSelectAll,
    isPersonalVip,
    vipStatusLoaded,
    previewIndex,
    isSearchMode,
    lastPageCond,
    filter,
    setPage,
    setFilter,
    resetFilter,
    toggleSelectPhoto,
    toggleSelectAll,
    clearSelection,
    setPreviewIndex,
    exitSearchMode,
    fetchVipStatus,
    fetchSpaceInfo,
    fetchWatermarks,
    fetchPhotos,
    searchPhotos,
    deletePhotos,
    downloadPhotos,
    downloadSinglePhoto,
    uploadPhoto
  } = usePersonalSpace()

  const [uploadingCount, setUploadingCount] = useState(0)

  const [vipGuardVisible, setVipGuardVisible] = useState(false)
  const [vipFromPlace, setVipFromPlace] = useState('')
  const [downloadModalVisible, setDownloadModalVisible] = useState(false)

  // 让 antd message 弹在视口竖直中间（默认是顶部 24px）
  useEffect(() => {
    message.config({ top: Math.max(0, window.innerHeight / 2 - 20) })
    return () => message.config({ top: 24 })
  }, [])

  // 初始化：会员状态 + 空间信息 + 照片列表
  useEffect(() => {
    fetchVipStatus()
    fetchSpaceInfo()
    fetchWatermarks()
  }, [fetchVipStatus, fetchSpaceInfo, fetchWatermarks])

  // 全量照片：page 变化时重新拉取（仅非搜索模式）
  useEffect(() => {
    if (!isSearchMode) {
      fetchPhotos()
    }
  }, [fetchPhotos, isSearchMode, page])

  // 曝光埋点：页面数据加载完成后上报一次
  const exposureTracked = useRef(false)
  useEffect(() => {
    if (!exposureTracked.current && vipStatusLoaded && !loading) {
      exposureTracked.current = true
      sensors.track('other_b7765_exposure', {
        isPersonalVip,
        photosNum: total || 0,
        track_sign: 'aother.b7765.exposure.other_b7765_exposure',
        requirement_id: 608
      })
    }
  }, [vipStatusLoaded, loading, isPersonalVip, total])

  // 点击埋点：个人空间主页点击事件
  const trackClick = useCallback(
    (clickItem: string) => {
      sensors.track('other_b7765_d7769_click', {
        clickItem,
        isPersonalVip,
        photosNum: total || 0,
        track_sign: 'aother.b7765.d7769.click.other_b7765_d7769_click',
        requirement_id: 608
      })
    },
    [isPersonalVip, total]
  )

  // 批量下载埋点
  const FOLDER_RULE_LABEL: Record<string, string> = {
    none: '不归类',
    date: '拍摄日期',
    address: '拍摄地点',
    watermark: '拍摄水印'
  }
  const NAMING_FIELD_LABEL: Record<string, string> = {
    photoNumber: '照片编号',
    time: '拍摄日期',
    address: '拍摄地点',
    watermark: '水印名称'
  }
  const trackBatchDownload = useCallback(
    (actionType: string, extra?: Record<string, any>) => {
      const params: Record<string, any> = {
        actionType,
        isPersonalVip,
        photosNum: selectedIds.length,
        track_sign: 'aother.b7765.d7774.completed.other_b7765_d7774_completed',
        requirement_id: 608
      }
      if (actionType === 'download' && extra) {
        params.classification = extra.classification
        params.namingMethod = extra.namingMethod
      }
      sensors.track('other_b7765_d7774_completed', params)
    },
    [isPersonalVip, selectedIds.length]
  )

  const guardVip = useCallback(
    (action: () => void, fromPlace?: string) => {
      if (!isPersonalVip) {
        setVipFromPlace(fromPlace || '')
        setVipGuardVisible(true)
        return
      }
      action()
    },
    [isPersonalVip]
  )

  const trackVipModal = useCallback(
    (actionType: string) => {
      sensors.track('other_b7765_d8082_completed', {
        actionType,
        fromPlace: vipFromPlace,
        track_sign: 'aother.b7765.d8082.completed.other_b7765_d8082_completed',
        requirement_id: 608
      })
    },
    [vipFromPlace]
  )

  // 重置：退出搜索模式，回到全量列表
  const handleReset = useCallback(() => {
    guardVip(() => {
      resetFilter()
      exitSearchMode()
    }, 'reset')
  }, [guardVip, resetFilter, exitSearchMode])

  // 查询：调用搜索接口
  const handleSearch = useCallback(
    (filter: {
      keyword: string
      startTime?: number
      endTime?: number
      watermarkIds: string[]
    }) => {
      guardVip(async () => {
        setFilter({
          keyword: filter.keyword,
          startTime: filter.startTime,
          endTime: filter.endTime,
          watermarkIds: filter.watermarkIds
        })
        const photoNum = await searchPhotos({
          keyword: filter.keyword,
          startTime: filter.startTime,
          endTime: filter.endTime
        })
        if (photoNum !== undefined) {
          const searchContent = filter.keyword
            ? filter.keyword
                .trim()
                .split(/[\s,，]+/)
                .filter(Boolean)
            : []
          let dateOffset = 0
          const timeRule: string[] = []
          if (filter.startTime && filter.endTime) {
            const start = dayjs(filter.startTime)
            const end = dayjs(filter.endTime)
            dateOffset = end.diff(start, 'days') + 1
            timeRule.push(
              `${start.format('YYYY年M月D日')}-${end.format('YYYY年M月D日')}`
            )
          }
          sensors.track('other_b8083_completed', {
            searchContent,
            photoNum,
            dateOffset,
            watermarkNum: filter.watermarkIds.length,
            isAllWatermark:
              watermarks.length > 0 &&
              filter.watermarkIds.length === watermarks.length,
            timeRule,
            track_sign: 'aother.b8083.completed.other_b8083_completed',
            requirement_id: 608
          })
        }
      }, 'search')
    },
    [guardVip, setFilter, searchPhotos, watermarks.length]
  )

  // 搜索模式加载更多：必须透传当前 filter，否则游标分页会回退到"无条件"，
  // 拼出去的列表与首页不一致
  const handleLoadMore = useCallback(() => {
    searchPhotos(
      {
        keyword: filter.keyword,
        startTime: filter.startTime,
        endTime: filter.endTime
      },
      true
    )
  }, [searchPhotos, filter.keyword, filter.startTime, filter.endTime])

  const handleBatchDelete = useCallback(() => {
    guardVip(() => {
      if (selectedIds.length === 0) {
        message.warning('请先选择照片')
        return
      }
      if (selectedIds.length > 999) {
        message.warning('批量删除一次最多可删除999张照片')
        return
      }
      Modal.confirm({
        title: `是否删除${selectedIds.length}张照片/视频？`,
        content: '照片删除后不支持找回，请谨慎操作。',
        okText: '删除',
        cancelText: '取消',
        okType: 'danger',
        centered: true,
        onOk: () => {
          // 不返回 Promise，让弹窗立即关闭
          deletePhotos(selectedIds)
        }
      })
    }, 'batchDelete')
  }, [guardVip, selectedIds, deletePhotos])

  const handleOpenDownload = useCallback(() => {
    guardVip(() => {
      if (selectedIds.length === 0) {
        message.warning('请先选择照片')
        return
      }
      trackBatchDownload('showPop')
      setDownloadModalVisible(true)
    }, 'batchDownload')
  }, [guardVip, selectedIds])

  const handleConfirmDownload = useCallback(
    (config: DownloadConfig) => {
      trackBatchDownload('download', {
        classification:
          FOLDER_RULE_LABEL[config.folderRule] || config.folderRule,
        namingMethod: config.namingFields.map((f) => NAMING_FIELD_LABEL[f] || f)
      })
      setDownloadModalVisible(false)
      downloadPhotos(selectedIds, config)
    },
    [selectedIds, downloadPhotos, trackBatchDownload]
  )

  const handlePreviewDelete = useCallback(
    async (id: string) => {
      // 先按"删除后"的长度计算下一个 previewIndex，再触发删除。
      // 不能在 await 之后读 photos.length —— 那是 render 时捕获的旧值，
      // React 还没重渲染，依然是删除前的长度。
      const newLen = photos.length - 1
      if (newLen <= 0) {
        setPreviewIndex(-1)
      } else if (previewIndex >= newLen) {
        setPreviewIndex(newLen - 1)
      }
      await deletePhotos([id])
    },
    [deletePhotos, previewIndex, photos.length, setPreviewIndex]
  )

  const isNotVip = vipStatusLoaded && !isPersonalVip

  return (
    <div className={styles.page}>
      <SpaceHeader onTrack={trackClick} />
      <div className={styles.body}>
        {/* 标题 */}
        <h2 className={styles.title}>全部照片</h2>

        {/* 筛选栏 */}
        <FilterBar
          watermarks={watermarks}
          onSearch={handleSearch}
          onReset={handleReset}
          onTrack={trackClick}
        />

        {/* 照片列表区 */}
        <div className={styles.listCard}>
          {/* 工具栏 */}
          <div className={styles.toolbar}>
            <div className={styles.toolbarLeft}>
              <span className={styles.statText}>
                共筛选出 <strong>{total}</strong> 张照片
              </span>
              <Checkbox
                indeterminate={selectedIds.length > 0 && !isSelectAll}
                checked={isSelectAll}
                onChange={() => {
                  trackClick('chooseAll')
                  toggleSelectAll()
                }}
              >
                全选
              </Checkbox>
              {selectedIds.length > 0 && (
                <span className={styles.selectedInfo}>
                  已选择 <strong>{selectedIds.length}</strong> 张{' '}
                  <a
                    onClick={() => {
                      trackClick('cancelChoose')
                      clearSelection()
                    }}
                    className={styles.cancelLink}
                  >
                    取消选择
                  </a>
                </span>
              )}
            </div>
            <div className={styles.toolbarRight}>
              <Button
                className={styles.noStickyFocus}
                onClick={() => {
                  trackClick('batchDelete')
                  handleBatchDelete()
                }}
              >
                批量删除
              </Button>
              <Button
                className={styles.noStickyFocus}
                onClick={() => {
                  trackClick('batchDownload')
                  handleOpenDownload()
                }}
              >
                批量下载
              </Button>
              <Button
                className={styles.noStickyFocus}
                onClick={() => {
                  trackClick('uploadLocalPhotos')
                  Modal.confirm({
                    title: '上传照片/视频 正在开发中',
                    content: '如有需要可点击下方催促开发',
                    okText: '催促开发',
                    cancelText: '取消',
                    centered: true,
                    onOk: () => {
                      message.info('已收到反馈')
                    }
                  })
                }}
              >
                上传照片/视频
              </Button>
            </div>
          </div>

          {/* 照片网格 */}
          <PhotoGrid
            photos={photos}
            selectedIds={selectedIds}
            loading={loading}
            onToggleSelect={(id) => {
              const willSelect = !selectedIds.includes(id)
              trackClick(willSelect ? 'chooseOnePhoto' : 'cancelChooseOnePhoto')
              toggleSelectPhoto(id)
            }}
            onClickPhoto={(index) => {
              trackClick('photo')
              guardVip(() => setPreviewIndex(index), 'photo')
            }}
          />

          {/* 全量模式：Pagination 翻页 */}
          {!isSearchMode && total > pageSize && (
            <div className={styles.pagination}>
              <Pagination
                current={page}
                total={total}
                pageSize={pageSize}
                showSizeChanger={false}
                onChange={(p) => {
                  trackClick('changePage')
                  setPage(p)
                  clearSelection()
                  window.scrollTo(0, 0)
                }}
                showTotal={(t) => `共 ${t} 张`}
              />
            </div>
          )}

          {/* 搜索模式：游标加载更多 */}
          {isSearchMode && lastPageCond && (
            <div className={styles.pagination}>
              <Button
                onClick={() => {
                  trackClick('loadMore')
                  handleLoadMore()
                }}
                loading={loading}
              >
                加载更多
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* 照片预览 */}
      {previewIndex >= 0 && (
        <PhotoPreview
          photos={photos}
          total={total}
          currentIndex={previewIndex}
          isPersonalVip={isPersonalVip}
          onClose={() => setPreviewIndex(-1)}
          onIndexChange={setPreviewIndex}
          onDelete={handlePreviewDelete}
          onDownload={downloadSinglePhoto}
        />
      )}

      <VipGuard
        open={vipGuardVisible}
        onClose={() => setVipGuardVisible(false)}
        onTrack={trackVipModal}
      />

      <DownloadModal
        open={downloadModalVisible}
        count={selectedIds.length}
        onCancel={() => {
          trackBatchDownload('closePop')
          setDownloadModalVisible(false)
        }}
        onConfirm={handleConfirmDownload}
      />
    </div>
  )
}

export default PersonalSpace
